// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import axios from 'axios';
import Decimal from 'decimal.js';
import * as sharetribeSdk from 'sharetribe-flex-sdk';
import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const parseLocalApiResponse = res => {
  const contentTypeHeader = res.headers.get('Content-Type');
  const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

  if (res.status >= 400) {
    if (contentType === 'application/transit+json') {
      return res

        .text()
        .then(deserialize)
        .then(data => {
          let e = new Error();
          e = Object.assign(e, { status: res.status }, data);
          throw e;
        });
    }

    return res.json().then(data => {
      let e = new Error();
      e = Object.assign(e, { status: res.status }, data);
      throw e;
    });
  }

  if (contentType === 'application/transit+json') {
    return res.text().then(deserialize);
  }
  if (contentType === 'application/json') {
    return res.json();
  }
  return res.text();
};

const post = (path, body, baseUrl = apiBaseUrl(), requestOptions = {}) => {
  const { contentType = 'application/transit+json', serializeBody = serialize } = requestOptions;
  const url = `${baseUrl}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': contentType,
    },
    body: serializeBody(body),
  };
  return window.fetch(url, options).then(parseLocalApiResponse);
};

export const get = path => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'GET',
    credentials: 'include',
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    }

    if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getUserToken = () => {
  const cookieTokenStore = sharetribeSdk.tokenStore.browserCookieStore({
    clientId: config.sdk.clientId,
    secure: config.usingSSL,
  });
  return cookieTokenStore.getToken();
};

export const setUserToken = token => {
  const cookieTokenStore = sharetribeSdk.tokenStore.browserCookieStore({
    clientId: config.sdk.clientId,
    secure: config.usingSSL,
  });
  return cookieTokenStore.setToken(token);
};

export const verifyRecaptcha = body => {
  return post('/api/auth/verify-recaptcha', body);
};

// Send OTP code with optional context for chat session tracking
export const sendOtpCode = (email, context = null) => {
  return post('/api/auth/otp/send-code', { email, context });
};

// Verify code - returns idpToken directly (5 min expiry)
export const verifyOtpCode = (email, code) => {
  return post('/api/auth/otp/verify-code', { email, code });
};

// Login with OTP idpToken (for existing users)
// Must go through server-side because sdk.loginWithIdp requires clientSecret
export const loginWithOtp = idpToken => {
  return post('/api/auth/otp/login', { idpToken });
};

// Signup with OTP idpToken (for new users)
// Must go through server-side because sdk.createWithIdp/loginWithIdp require clientSecret
export const signupWithOtp = (idpToken, userData) => {
  return post('/api/auth/otp/signup', { idpToken, ...userData });
};

// Sync post-signup Sharetribe permissions for the current authenticated user.
export const syncSignupPermissions = () => {
  return post('/api/auth/sync-signup-permissions', {});
};

export const stripeCreate = body => {
  return post('/api/stripe/create', body);
};

export const stripeCancel = body => {
  return post('/api/stripe/cancel', body);
};

export const stripeCapture = body => {
  return post('/api/stripe/capture', body);
};

export const stripeRefund = body => {
  return post('/api/stripe/refund', body);
};

export const stripePayout = body => {
  return post('/api/stripe/payout', body);
};

export const stripeRetrieve = body => {
  return post('/api/stripe/retrieve', body);
};

export const compareLineItemTotals = body => {
  return post('/api/compare-line-item-totals', body);
};

export const updateTransactionMetadata = body => {
  return post('/api/update-transaction-metadata', body);
};

export const sendInquiryApi = body => {
  return post('/api/send-inquiry', body);
};

export const sendMessageApi = body => {
  return post('/api/send-message', body);
};

// Endpoint from new UI pages/api
export async function refreshUserToken(refreshToken) {
  const response = await fetch('https://flex-api.sharetribe.com/v1/auth/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id: config.sdk.clientId,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }

  const data = await response.json();
  return data;
}

const isUnauthorizedError = error => {
  const errors = error?.data?.errors || [];

  return (
    error?.status === 401 ||
    error?.statusText === 'Unauthorized' ||
    errors.some(e => e?.status === 401 || e?.status === '401')
  );
};

export const runWithUserTokenRefresh = async operation => {
  try {
    return await operation();
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      throw error;
    }

    const token = getUserToken();

    if (!token?.refresh_token) {
      throw error;
    }

    const newToken = await refreshUserToken(token.refresh_token);
    setUserToken(newToken);

    return operation();
  }
};

// landtrust-ui: /stripe/create-subscription
// landtrust proxy (local, dev, prod): /stripe/lt-plus/create-subscription
const LT_PLUS_SUBSCRIPTION_PATH = '/stripe/lt-plus/create-subscription';

export const stripeCreateSubscription = async (body, hasAttemptedRefresh = false) => {
  const token = getUserToken();

  const response = await fetch(
    `${process.env.REACT_APP_NEXT_API_URL}${LT_PLUS_SUBSCRIPTION_PATH}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify(body || {}),
    }
  );

  if (response.status === 401 && !hasAttemptedRefresh) {
    const newToken = await refreshUserToken(token.refresh_token);

    setUserToken(newToken);
    return stripeCreateSubscription(body, true);
  }

  if (response.status === 401 && hasAttemptedRefresh) {
    throw new Error('Unauthorized after token refresh attempt.');
  }

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return response.json();
};

// landtrust-ui: /stripe/create-landowner-insurance-subscription
// landtrust proxy (local, dev, prod): /stripe/landowner-insurance/create-subscription
const LANDOWNER_INSURANCE_SUBSCRIPTION_PATH = '/stripe/landowner-insurance/create-subscription';
const LANDOWNER_INSURANCE_CHECK_PROMO_CODE_PATH = '/stripe/landowner-insurance/check-promo-code';
const LANDOWNER_INSURANCE_UPDATE_PAYMENT_METHOD_PATH =
  '/stripe/landowner-insurance/update-payment-method';

export const checkLandownerInsurancePromoCode = async body => {
  const response = await fetch(
    `${process.env.REACT_APP_NEXT_API_URL}${LANDOWNER_INSURANCE_CHECK_PROMO_CODE_PATH}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || {}),
    }
  );

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return response.json();
};

export const createLandownerInsuranceSubscription = async (body, hasAttemptedRefresh = false) => {
  const token = getUserToken();

  const response = await fetch(
    `${process.env.REACT_APP_NEXT_API_URL}${LANDOWNER_INSURANCE_SUBSCRIPTION_PATH}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify(body || {}),
    }
  );

  if (response.status === 401 && !hasAttemptedRefresh) {
    const newToken = await refreshUserToken(token.refresh_token);

    setUserToken(newToken);
    return createLandownerInsuranceSubscription(body, true);
  }

  if (response.status === 401 && hasAttemptedRefresh) {
    throw new Error('Unauthorized after token refresh attempt.');
  }

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return response.json();
};

export const updateLandownerInsurancePaymentMethod = async (body, hasAttemptedRefresh = false) => {
  const token = getUserToken();

  const response = await fetch(
    `${process.env.REACT_APP_NEXT_API_URL}${LANDOWNER_INSURANCE_UPDATE_PAYMENT_METHOD_PATH}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.access_token}`,
      },
      body: JSON.stringify(body || {}),
    }
  );

  if (response.status === 401 && !hasAttemptedRefresh) {
    const newToken = await refreshUserToken(token.refresh_token);

    setUserToken(newToken);
    return updateLandownerInsurancePaymentMethod(body, true);
  }

  if (response.status === 401 && hasAttemptedRefresh) {
    throw new Error('Unauthorized after token refresh attempt.');
  }

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return response.json();
};

export const callGraphQLApi = async (query, variables, hasAttemptedRefresh = false) => {
  const token = getUserToken();

  try {
    const response = await axios.post(
      config.graphQLapi,
      {
        query,
        variables,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.access_token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response?.status === 401 && !hasAttemptedRefresh) {
      const newToken = await refreshUserToken(token.refresh_token);
      setUserToken(newToken);
      return callGraphQLApi(query, variables, true);
    }

    if (error.response?.status === 401 && hasAttemptedRefresh) {
      throw new Error('Unauthorized after token refresh attempt.');
    }

    if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors[0].message);
    }

    throw error;
  }
};

export const cancelMembershipSubscription = async (body, hasAttemptedRefresh = false) => {
  const token = getUserToken();

  const response = await fetch(`${process.env.REACT_APP_NEXT_API_URL}/stripe/cancel-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token.access_token}`,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401 && !hasAttemptedRefresh) {
    const newToken = await refreshUserToken(token.refresh_token);

    setUserToken(newToken);
    return cancelMembershipSubscription(body, true);
  }

  if (response.status === 401 && hasAttemptedRefresh) {
    // If a refresh has already been attempted, throw a specific error
    throw new Error('Unauthorized after token refresh attempt.');
  }

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text);
  }

  return response.json();
};
